import { useMemo, useState } from 'react';
import { MAX_FILE_SIZE } from '@/consts/const';
import { useUploadFile } from '@/context/UploadFileProvider';
import { isFileSizeValid } from '@/hooks/useFileDrop/utils/validations';
import { IconButton } from '../buttons/IconButton';
import { PreviewFileCard } from '../cards/PreviewFileCard';
import { UploadFileProgressItem } from './components/UploadFileProgressItem';
import { UploadStatusHeader } from './components/UploadStatusHeader';
import ChevronDownIcon from '/public/icons/chevron-down.svg';
import CloseIcon from '/public/icons/cross.svg';

interface UploadManagerProps {
  renderActions: ({ isOpen, isDisabled }: { isOpen: boolean; isDisabled: boolean }) => React.ReactNode;
}

export function UploadManager({ renderActions }: UploadManagerProps) {
  const [isOpenContent, setIsOpenContent] = useState(true);
  const { uploads, entityLabel, onCancelUpload, isOpenUploadManager, onCloseUploadManager } = useUploadFile();

  const onOpenImagesContent = () => {
    setIsOpenContent(!isOpenContent);
  };

  const isUploading = useMemo(() => uploads.some((upload) => upload.status === 'uploading'), [uploads]);
  const isSuccess = useMemo(() => uploads.every((upload) => upload.status === 'success'), [uploads]);
  const totalSuccessUploads = useMemo(() => uploads.filter((upload) => upload.status === 'success').length, [uploads]);
  const sortedUploads = useMemo(() => uploads.sort((a, b) => b.progress - a.progress), [uploads]);
  const hasUnsupportedFiles = useMemo(
    () => uploads.some((upload) => !isFileSizeValid(upload.file, MAX_FILE_SIZE)),
    [uploads]
  );

  if (!isOpenUploadManager) return null;

  return (
    <div className="fixed z-[9999] bottom-[30px] right-[30px] flex flex-col items-center justify-center max-w-[416px] min-w-[416px] max-h-[512px] px-6 py-[30px] rounded-[14px] shadow-[0px_0px_24px_0px_rgba(6,32,96,0.14)] bg-white">
      <div className="flex items-center justify-between w-full">
        <UploadStatusHeader
          entityLabel={entityLabel}
          isSuccess={isSuccess}
          isOpen={isOpenContent}
          isUploading={isUploading}
          totalUploads={uploads.length}
          totalSuccessUploads={totalSuccessUploads}
        />
        <div className="flex items-center gap-3">
          <IconButton
            type="button"
            onClick={onOpenImagesContent}
            icon={<ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpenContent ? 'rotate-180' : ''}`} />}
          />
          <IconButton type="button" onClick={onCloseUploadManager} icon={<CloseIcon className="w-4 h-4" />} />
        </div>
      </div>
      <div
        className={`overflow-hidden transition-[max-height] duration-300 ease-in-out w-full ${isOpenContent ? 'max-h-[324px] mt-6' : 'max-h-0'
          }`}
      >
        {isUploading || isSuccess ? (
          <ul className="w-full flex flex-col gap-5 overflow-y-auto max-h-[324px] pb-2">
            {sortedUploads.map((upload) => (
              <UploadFileProgressItem onCancel={onCancelUpload} key={upload.file.name} upload={upload} />
            ))}
          </ul>
        ) : (
          <div className="grid grid-cols-4 gap-3 w-full overflow-y-auto overflow-x-hidden max-h-[324px] px-3">
            {uploads.map((upload) => (
              <PreviewFileCard key={upload.file.name} file={upload.file} />
            ))}
          </div>
        )}
      </div>
      {renderActions({
        isOpen: isOpenContent,
        isDisabled: hasUnsupportedFiles,
      })}
    </div>
  );
}